"use client"

import {
  ComposedChart as RComposedChart, Bar, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts"
import {
  type BaseChartProps, DEFAULT_PALETTE, inferSeries, colorFor,
} from "./types"

export type ComposedSeriesKind = "bar" | "line" | "area"

export interface ComposedChartProps extends BaseChartProps {
  /** Per-series rendering choice. Series listed here override the default
   *  rotation (bar → line → area → bar …). */
  seriesKinds?: Record<string, ComposedSeriesKind>
  /** Two Y axes — first half on left, rest on right. */
  biaxial?: boolean
  /** Stack bar/area series in the same group. */
  stacked?: boolean
}

const ROTATION: ComposedSeriesKind[] = ["bar", "line", "area"]

export function ComposedChart({
  data, series, xKey = "name", palette = DEFAULT_PALETTE, colorMap,
  showLegend = true, showGrid = true, showTooltip = true,
  xLabel, yLabel, compact, className, style, height = "100%", width = "100%",
  seriesKinds, biaxial, stacked,
}: ComposedChartProps) {
  const seriesList = series ?? inferSeries(data, xKey)
  return (
    <ResponsiveContainer width={width} height={height} className={className} style={style}>
      <RComposedChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
        {showGrid && !compact && <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.18} />}
        {!compact && <XAxis dataKey={xKey} tick={{ fontSize: 10.5 }} label={xLabel ? { value: xLabel, position: "insideBottom", offset: -4 } : undefined} interval={0} angle={-15} dy={6} height={50} />}
        {!compact && <YAxis yAxisId="left" tick={{ fontSize: 10.5 }} label={yLabel ? { value: yLabel, angle: -90, position: "insideLeft" } : undefined} />}
        {biaxial && !compact && <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10.5 }} />}
        {showTooltip && <Tooltip cursor={{ fill: "hsl(var(--foreground) / 0.06)", stroke: "hsl(var(--muted-foreground))", strokeOpacity: 0.35 }} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 11, color: "hsl(var(--card-foreground))" }} />}
        {showLegend && !compact && <Legend wrapperStyle={{ fontSize: 11 }} />}
        {seriesList.map((name, i) => {
          const kind = seriesKinds?.[name] || ROTATION[i % ROTATION.length]
          const color = colorFor(name, i, palette, colorMap)
          const yAxisId = biaxial ? (i < seriesList.length / 2 ? "left" : "right") : "left"
          if (kind === "bar") return <Bar key={name} dataKey={name} fill={color} yAxisId={yAxisId} stackId={stacked ? "a" : undefined} radius={[3, 3, 0, 0]} isAnimationActive={false} />
          if (kind === "line") return <Line key={name} type="monotone" dataKey={name} stroke={color} strokeWidth={2.5} dot={{ r: 3 }} yAxisId={yAxisId} isAnimationActive={false} />
          return <Area key={name} type="monotone" dataKey={name} stroke={color} fill={color} fillOpacity={0.35} yAxisId={yAxisId} stackId={stacked ? "a" : undefined} isAnimationActive={false} />
        })}
      </RComposedChart>
    </ResponsiveContainer>
  )
}
